import React, { useState, useEffect } from 'react';
import { photosAPI } from '../services/api';
import './UserAvatar.css';

interface UserAvatarProps {
  userId: string | number;
  userName?: string;
  size?: 'tiny' | 'small' | 'medium' | 'large' | 'extra-large';
  className?: string;
  onClick?: () => void;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ 
  userId, 
  userName, 
  size = 'medium',
  className = '',
  onClick
}) => {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadAvatar = async () => {
      // Преобразуем userId в число, если это строка
      const numericUserId = typeof userId === 'string' ? parseInt(userId, 10) : userId;
      
      try {
        setLoading(true);
        setError(false);
        
        console.log(`🔄 Загружаем аватар для пользователя: ${userId} (тип: ${typeof userId})`);
        
        if (isNaN(numericUserId)) {
          console.error(`❌ Неверный userId: ${userId}`);
          setError(true);
          return;
        }
        
        const response = await photosAPI.getUserPhotos(numericUserId);
        
        if (response.success && response.photos && response.photos.length > 0) {
          // Берем первую (самую большую) фотографию
          const firstPhoto = response.photos[0];
          console.log(`✅ Найдена фотография для пользователя ${numericUserId}:`, firstPhoto);
          
          // Формируем полный URL для изображения
          const baseURL = process.env.REACT_APP_API_URL || 'https://api.denisphones.org';
          const fullImageUrl = `${baseURL}${firstPhoto.url}`;
          console.log(`🖼️ Полный URL изображения: ${fullImageUrl}`);
          
          setAvatarUrl(fullImageUrl);
        } else {
          console.log(`❌ Фотографии не найдены для пользователя ${numericUserId}`);
          setError(true);
        }
      } catch (err) {
        console.error(`❌ Ошибка загрузки аватара для пользователя ${numericUserId}:`, err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      loadAvatar();
    }
  }, [userId]);

  const getInitials = () => {
    if (userName) {
      return userName.charAt(0).toUpperCase();
    }
    return '?';
  };

  const getSizeClass = () => {
    switch (size) {
      case 'tiny': return 'avatar-tiny';
      case 'small': return 'avatar-small';
      case 'large': return 'avatar-large';
      case 'extra-large': return 'avatar-extra-large';
      default: return 'avatar-medium';
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const avatarClasses = `user-avatar ${getSizeClass()} ${className} ${onClick ? 'avatar-clickable' : ''}`;

  if (loading) {
    return (
      <div className={`${avatarClasses} avatar-loading`} onClick={handleClick}>
        <div className="avatar-spinner"></div>
      </div>
    );
  }

  if (error || !avatarUrl) {
    return (
      <div className={`${avatarClasses} avatar-fallback`} onClick={handleClick}>
        {getInitials()}
      </div>
    );
  }

  return (
    <div className={`${avatarClasses} avatar-image`} onClick={handleClick}>
      <img 
        src={avatarUrl} 
        alt={`Аватар ${userName || 'пользователя'}`}
        onError={() => setError(true)}
        loading="lazy"
      />
    </div>
  );
};

export default UserAvatar;
